import sortBy from "lodash-es/sortBy";
import {
  DecileClassification,
  PolicyRecommendation,
  ProductLevel,
} from "../../graphQL/types/shared";
import { newHSColorMap } from "../../Utils";
import { getWeightsForPolicyCombination } from "../feasibilityChart/transform";
import { numTopProductsToBeHighlighted } from "../feasibilityChart/transform";
import { PolicyDimension } from "../Utils";
import {
  FetchedCountryProductYearDatum,
  FetchedProductYearDatum,
  FetchedTreeDatum,
} from "./index";
import { TableDatum } from "./Table";

interface Input {
  products: FetchedTreeDatum[];
  allProductYear: FetchedProductYearDatum[];
  allCountryProductYear: FetchedCountryProductYearDatum[];
  policyDimension: PolicyDimension;
  policyRecommendation: PolicyRecommendation;
  countryECI: number;
  gdpPerCaptia: number;
}

interface FilteredDatum {
  id: string;
  section: string;
  level: ProductLevel;
  shortLabel: string;
  code: string;
  globalExportValue: number;
  // Not filtering out `null` values here because we want the list of items
  // shown in the table to be a strict subset of the items highlightec in the
  // feasibility chart:
  globalExportValueChange: number | null;

  normalizedOpportunityGainDecileClassification: DecileClassification;
  normalizedDistanceDecileClassification: DecileClassification;
  normalizedPCIDecileClassification: DecileClassification;
  pci: number;
  normalizedPCI: number;
  normalizedDistance: number;
  normalizedOpportunityGain: number;
  score: number;
}

const transform = (input: Input) => {
  const {
    products,
    policyDimension,
    policyRecommendation,
    countryECI,
    gdpPerCaptia,
    allProductYear,
    allCountryProductYear,
  } = input;
  const {
    distance: distanceWeight,
    complexity: complexityWeight,
    opportunityGain: opportunityGainWeight,
  } = getWeightsForPolicyCombination(policyRecommendation, policyDimension);
  const filteredData: FilteredDatum[] = [];
  for (const product of products) {
    const {
      product: {
        id,
        shortName,
        level,
        code,
        topLevelParent: { id: section },
        neverShow,
      },
      normalizedPCI,
      normalizedDistance,
      normalizedOpportunityGain,
    } = product;

    const productYearDatum = allProductYear.find((d) => d.product.id === id);
    let globalExportValue: number | null = null;
    let pci: number | null = null;
    let globalExportValueChange: number | null = null;
    if (productYearDatum) {
      globalExportValue = productYearDatum.globalExportValue;
      pci = productYearDatum.pci;
      globalExportValueChange = productYearDatum.globalExportValueChange;
    }

    const countryProductYearDatum = allCountryProductYear.find(
      (d) => d.product.id === id
    );
    let normalizedOpportunityGainDecileClassification: null | DecileClassification = null;
    let normalizedDistanceDecileClassification: null | DecileClassification = null;
    let normalizedPCIDecileClassification: null | DecileClassification = null;
    if (countryProductYearDatum) {
      normalizedOpportunityGainDecileClassification =
        countryProductYearDatum.normalizedOpportunityGainDecileClassification;
      normalizedDistanceDecileClassification =
        countryProductYearDatum.normalizedDistanceDecileClassification;
      normalizedPCIDecileClassification =
        countryProductYearDatum.normalizedPCIDecileClassification;
    }

    if (neverShow !== true) {
      const score =
        (normalizedDistance !== null ? normalizedDistance : 0) *
          distanceWeight +
        (normalizedPCI !== null ? normalizedPCI : 0) * complexityWeight +
        (normalizedOpportunityGain !== null ? normalizedOpportunityGain : 0) *
          opportunityGainWeight;

      const filtered: FilteredDatum = {
        id,
        section,
        level,
        shortLabel: shortName,
        code,
        score,
        normalizedPCIDecileClassification:
          normalizedPCIDecileClassification !== null
            ? normalizedPCIDecileClassification
            : DecileClassification.NA,
        normalizedDistanceDecileClassification:
          normalizedDistanceDecileClassification !== null
            ? normalizedDistanceDecileClassification
            : DecileClassification.NA,
        normalizedOpportunityGainDecileClassification:
          normalizedOpportunityGainDecileClassification !== null
            ? normalizedOpportunityGainDecileClassification
            : DecileClassification.NA,
        globalExportValue: globalExportValue !== null ? globalExportValue : 0,
        normalizedDistance:
          normalizedDistance !== null ? normalizedDistance : 0,
        normalizedOpportunityGain:
          normalizedOpportunityGain !== null ? normalizedOpportunityGain : 0,
        normalizedPCI: normalizedPCI !== null ? normalizedPCI : 0,
        pci: pci !== null ? pci : 0,
        globalExportValueChange:
          globalExportValueChange !== null ? globalExportValueChange : 0,
      };
      filteredData.push(filtered);
    }
  }
  const filteredAndSortedByScore = sortBy(
    filteredData,
    (elem) => elem.score
  ).reverse();
  let topProductCounter = 0;

  const topProducts: TableDatum[] = [];
  let productCounter = 0;
  while (
    productCounter < filteredAndSortedByScore.length &&
    topProductCounter < numTopProductsToBeHighlighted
  ) {
    const filteredDatum = filteredAndSortedByScore[productCounter];
    const {
      pci,
      shortLabel,
      id,
      section,
      code,
      score,
      normalizedDistanceDecileClassification,
      normalizedOpportunityGainDecileClassification,
      normalizedPCIDecileClassification,
      globalExportValue,
      globalExportValueChange,
      normalizedPCI,
      normalizedDistance,
      normalizedOpportunityGain,
    } = filteredDatum;

    const gdpPerCapitaCeiling = 6000;
    const ceilingRange = 1.75;
    const pciCeiling =
      gdpPerCaptia <= gdpPerCapitaCeiling ? countryECI + ceilingRange : 1000;

    if (topProductCounter < numTopProductsToBeHighlighted) {
      topProductCounter += 1;
      const color = newHSColorMap.get(section);
      if (color === undefined) {
        throw new Error("Cannot find color for section " + section);
      }

      const tableDatum: TableDatum = {
        id,
        color,
        code,
        score,
        name: shortLabel,
        complexityDecile: normalizedPCIDecileClassification,
        opportunityGainDecile: normalizedOpportunityGainDecileClassification,
        feasibilityDecile: normalizedDistanceDecileClassification,
        globalExportValue,
        globalExportValueChange:
          globalExportValueChange !== null ? globalExportValueChange : 0,
        normalizedPCI,
        normalizedOpportunityGain,
        normalizedFeasibility: normalizedDistance,
      };
      topProducts.push(tableDatum);
    }
    productCounter += 1;
  }
  return topProducts;
};

export default transform;
